import { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { leadsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import MyDayTab from '../components/leads/MyDayTab';
import LeadsListTab from '../components/leads/LeadsListTab';
import UploadTab from '../components/leads/UploadTab';
import LeadsAdminTab from '../components/leads/LeadsAdminTab';
import ReportsTab from '../components/leads/ReportsTab';
import LeadDetailModal from '../components/leads/LeadDetailModal';

export default function LeadsPage() {
  const { user } = useAuth();
  const isPreviewEnv = user?.app_env === 'preview';
  const [caps, setCaps] = useState(null);
  const [tab, setTab] = useState('my-day');
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadCaps = useCallback(async () => {
    try {
      const { data } = await leadsAPI.getCapabilities();
      setCaps(data);
      setTab(data.can_work ? 'my-day' : data.can_import ? 'upload' : 'leads');
    } catch (e) {
      toast.error('Failed to load Leads capabilities');
    }
  }, []);

  useEffect(() => {
    loadCaps();
    leadsAPI.updatePrefs(Intl.DateTimeFormat().resolvedOptions().timeZone).catch(() => {});
  }, [loadCaps]);

  if (!caps) return <div className="p-6 text-muted-foreground" data-testid="leads-loading">Loading Leads...</div>;

  const openLead = (id) => setSelectedLeadId(id);
  const closeLead = (shouldRefresh) => { setSelectedLeadId(null); if (shouldRefresh) setRefreshKey(k => k + 1); };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Leads</h1>
        <p className="text-muted-foreground mt-2 text-base">Cold leads, activity log and conversion — now inside Trade IQ.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="leads-tabs">
          {caps.can_work && <TabsTrigger value="my-day" data-testid="leads-tab-my-day">My Day</TabsTrigger>}
          <TabsTrigger value="leads" data-testid="leads-tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="reports" data-testid="leads-tab-reports">Reports</TabsTrigger>
          {caps.can_import && <TabsTrigger value="upload" data-testid="leads-tab-upload">Upload</TabsTrigger>}
          {caps.can_admin && <TabsTrigger value="admin" data-testid="leads-tab-admin">Admin</TabsTrigger>}
        </TabsList>

        {caps.can_work && (
          <TabsContent value="my-day">
            <MyDayTab key={refreshKey} onOpenLead={openLead} />
          </TabsContent>
        )}
        <TabsContent value="leads">
          <LeadsListTab key={refreshKey} caps={caps} onOpenLead={openLead} />
        </TabsContent>
        <TabsContent value="reports">
          <ReportsTab caps={caps} />
        </TabsContent>
        {caps.can_import && (
          <TabsContent value="upload">
            <UploadTab onDone={() => setRefreshKey(k => k + 1)} />
          </TabsContent>
        )}
        {caps.can_admin && (
          <TabsContent value="admin">
            <LeadsAdminTab key={refreshKey} caps={caps} isPreviewEnv={isPreviewEnv} />
          </TabsContent>
        )}
      </Tabs>

      {selectedLeadId && (
        <LeadDetailModal leadId={selectedLeadId} caps={caps} onClose={closeLead} />
      )}
    </div>
  );
}
